import type { ReactNode } from "react";
import { Link } from "react-router-dom";

import InfoPopover from "./InfoPopover";

type Props = {
  label: string;
  value: string;
  hint?: ReactNode;
  tone?: "default" | "positive" | "negative";
  /** Wenn gesetzt, wird die Card zu einem Link auf diese Route. */
  to?: string;
  /** Erklärt die KPI; erscheint als Popover unter der Card beim Hover/Fokus. */
  info?: ReactNode;
};

export default function KpiCard({
  label,
  value,
  hint,
  tone = "default",
  to,
  info,
}: Props) {
  const toneClasses =
    tone === "positive"
      ? "text-emerald-700"
      : tone === "negative"
        ? "text-rose-700"
        : "text-slate-900";
  const cardPadding = info ? "py-3 pl-4 pr-10" : "px-4 py-3";
  const cardClasses =
    `flex h-full flex-col rounded-lg border border-slate-200 bg-white ${cardPadding} shadow-sm`;
  const linkClasses = to
    ? "transition hover:border-emerald-300 hover:shadow-md"
    : "";

  const body = (
    <>
      <div className="text-xs uppercase tracking-wide text-slate-500">
        {label}
      </div>
      <div className={`mt-1 text-2xl font-semibold tabular-nums ${toneClasses}`}>
        {value}
      </div>
      {hint ? (
        <div className="mt-1 text-xs text-slate-500">{hint}</div>
      ) : null}
    </>
  );

  const card = to ? (
    <Link to={to} className={`${cardClasses} ${linkClasses}`}>
      {body}
    </Link>
  ) : (
    <div className={cardClasses}>{body}</div>
  );

  if (!info) {
    return card;
  }

  return (
    <div className="relative h-full">
      {card}
      <InfoPopover
        ariaLabel={`${label} Details`}
        triggerClassName="absolute right-2 top-2 z-10 inline-flex h-6 w-6 items-center justify-center rounded-full border border-slate-200 bg-white text-xs font-semibold text-slate-500 shadow-sm hover:border-emerald-200 hover:text-emerald-700 focus:outline-none focus:ring-2 focus:ring-emerald-500/30"
        popoverClassName="absolute left-0 right-0 top-full z-20 mt-1.5"
      >
        <div className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs leading-snug text-slate-700 shadow-lg ring-1 ring-slate-100">
          {info}
        </div>
      </InfoPopover>
    </div>
  );
}
